import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActivitiesHomeUrl, getHomeUrl, getSearchPageFromCityIdUrl } from 'core/utils/activityUrlGenerator';
import { resetState } from 'store/storeUtils';
import { routeTo } from 'store/slice/navigation';
import ContentArea from 'component/common/ContentArea/ContentArea';
import PageContainer from 'component/common/PageContainer/PageContainer';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import ErrorContent from 'component/page/error/ErrorContent';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesErrorComponentSeenEventActivitiesFieldsV3,
    ActivitiesPageContentErrorButtonClickEventActivitiesFieldsV3,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesErrorComponentSeenEventActivitiesManifestV3,
    ActivitiesPageContentErrorButtonClickEventActivitiesManifestV3,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';

interface Props {
    page: string;
    noBorderMargin?: boolean;
    contentStatus?: PageContentStatus;
    isTargetToSearchPage?: boolean;
}

const ErrorBoxSpacing: React.CSSProperties = { marginTop: '20vh', marginBottom: '20vh' };

export enum ErrorPageActionElementValue {
    NoResults = 'no results',
    TechnicalError = 'technical error',
}

export const getErrorPageCtaTargetUrl = (config: BootstrapConfig, isRedirectGenericErrorComponent: boolean, isTargetToSearchPage = false): string => {
    const homeUrl = getHomeUrl(config);
    const activityHomeUrl = getActivitiesHomeUrl(config);
    const searchUrl = config.geo && config.geo.cityId ? getSearchPageFromCityIdUrl(config.geo.cityId, config) : undefined;

    if (isRedirectGenericErrorComponent) {
        if (isTargetToSearchPage) {
            return searchUrl || activityHomeUrl;
        }
        return activityHomeUrl;
    }
    return homeUrl;
};

const StandardErrorPage: React.FC<Props> = React.memo((props) => {
    const config = useSelector(selectConfigFromBootstrap);
    const dispatch = useDispatch();
    const isRedirectGenericErrorComponent = isOn(FeatureName.RedirectGenericErrorComponent, config);

    React.useEffect(() => {
        trackElementEvent<ActivitiesErrorComponentSeenEventActivitiesFieldsV3>(
            ActivitiesErrorComponentSeenEventActivitiesManifestV3.ids,
            {},
            {
                action_element_value: props.contentStatus?.toString(),
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run seen tracking once
    }, []);

    const onButtonClick = () => {
        trackElementEvent<ActivitiesPageContentErrorButtonClickEventActivitiesFieldsV3>(
            ActivitiesPageContentErrorButtonClickEventActivitiesManifestV3.ids,
            {},
            { action_element_value: ErrorPageActionElementValue.TechnicalError },
        );
        resetState(dispatch);
        dispatch(routeTo({ path: getErrorPageCtaTargetUrl(config, isRedirectGenericErrorComponent, props.isTargetToSearchPage) }));
    };

    return (
        <PageContainer noBackground={true} data-element-name={`${props.page}-page`} display="flex" flex={1} overflowY="visible" style={props.noBorderMargin ? {} : ErrorBoxSpacing}>
            <ContentArea
                data-testid="activities-error-component"
                data-element-name="activities-error-component"
                py="16"
                px="24"
                display="flex"
                justifyContent="center"
                flexDirection="column"
                max={60}
                m="auto"
            >
                <ErrorContent
                    onClick={onButtonClick}
                    assetName="Error-Disable.svg"
                    assetWidth="140px"
                    buttonText={config.cms[130767]}
                    buttonHierarchy="secondary"
                    errorTitleText={config.cms[126985]}
                    errorDescriptionText={config.cms[126985]}
                />
            </ContentArea>
        </PageContainer>
    );
});
StandardErrorPage.displayName = 'StandardErrorPage';

export default StandardErrorPage;
